import React from 'react';
import { motion } from 'framer-motion';
import { Wrench, Clock, AlertCircle } from 'lucide-react';

interface MaintenanceModeProps {
  message?: string;
  estimatedDuration?: string;
}

const MaintenanceMode: React.FC<MaintenanceModeProps> = ({
  message = 'We are currently performing scheduled maintenance. Please check back soon.',
  estimatedDuration = 'a few minutes'
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-1/4 left-1/4 w-64 h-64 bg-jamaica-green/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-jamaica-green/10 rounded-full blur-2xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          />
        </div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center"
        >
          {/* Animated Icon */}
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-full mb-6 shadow-lg"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Wrench className="w-8 h-8 text-white" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-2xl font-bold text-gray-900 mb-4"
          >
            Under Maintenance
          </motion.h1>

          {/* Message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-gray-600 mb-6 leading-relaxed"
          >
            {message}
          </motion.p>

          {/* Duration Info */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-8"
          >
            <Clock className="w-4 h-4" />
            <span>Expected duration: {estimatedDuration}</span>
          </motion.div>

          {/* Progress Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mb-6"
          >
            <div className="flex justify-center space-x-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 bg-jamaica-green rounded-full"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.2,
                    ease: "easeInOut"
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="bg-gray-50 rounded-lg p-4 border"
          >
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-2">
              <AlertCircle className="w-4 h-4" />
              <span className="font-medium">Need immediate assistance?</span>
            </div>
            <p className="text-xs text-gray-500">
              Please contact our support team for urgent matters
            </p>
          </motion.div>

          {/* Refresh Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => window.location.reload()}
            className="mt-6 w-full bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white font-medium py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-200"
          >
            Check Again
          </motion.button>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="text-center text-xs text-gray-400 mt-6"
        >
          Thank you for your patience
        </motion.p>
      </div>
    </div>
  );
};

export default MaintenanceMode; 